import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/common/status-badge";
import { useHandoff, useLeadMessages, useUpdateHandoff } from "@/lib/queries";
import { formatDate, formatRelative } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/toast";
import type { Handoff } from "@/lib/types";

export function HandoffDrawer({ handoff, onClose }: { handoff: Handoff | null; onClose: () => void }) {
  return (
    <Sheet open={handoff !== null} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full max-w-2xl overflow-y-auto scrollbar-thin sm:max-w-2xl">
        {handoff && <HandoffDrawerBody handoffId={handoff.id} onClose={onClose} />}
      </SheetContent>
    </Sheet>
  );
}

function HandoffDrawerBody({ handoffId, onClose }: { handoffId: string; onClose: () => void }) {
  const { data: handoff, isLoading } = useHandoff(handoffId);
  const { data: messages } = useLeadMessages(handoff?.lead_id);
  const update = useUpdateHandoff();
  const toast = useToast();

  if (isLoading || !handoff) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-72" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  const resolve = async () => {
    try {
      await update.mutateAsync({ id: handoff.id, body: { status: "resolved" } });
      toast.success("Handoff resolved");
      onClose();
    } catch (err) {
      toast.error("Resolve failed", err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <SheetTitle>{handoff.full_name || "Handoff"}</SheetTitle>
      <SheetDescription className="mt-1 flex flex-wrap items-center gap-2">
        <StatusBadge status={handoff.status} />
        <span className="text-xs text-ink-faint">Opened {formatRelative(handoff.created_at)}</span>
      </SheetDescription>

      <section className="mt-5">
        <h4 className="text-xs font-medium uppercase tracking-wider text-ink-faint">Reason</h4>
        <p className="mt-1 text-sm text-ink">{handoff.reason || "—"}</p>
      </section>

      {handoff.summary && (
        <section className="mt-5">
          <h4 className="text-xs font-medium uppercase tracking-wider text-ink-faint">Summary</h4>
          <p className="mt-1 whitespace-pre-wrap text-sm text-ink">{handoff.summary}</p>
        </section>
      )}

      <section className="mt-5">
        <h4 className="text-xs font-medium uppercase tracking-wider text-ink-faint">Lead</h4>
        <dl className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <dt className="text-ink-muted">Phone</dt>
          <dd className="font-mono">{handoff.phone || "—"}</dd>
          <dt className="text-ink-muted">Resolved</dt>
          <dd>{handoff.resolved_at ? formatDate(handoff.resolved_at) : "—"}</dd>
        </dl>
      </section>

      <section className="mt-5 flex-1 overflow-hidden">
        <h4 className="text-xs font-medium uppercase tracking-wider text-ink-faint">Recent messages</h4>
        <div className="mt-1 max-h-72 overflow-y-auto rounded-md border border-line bg-surface-muted/40 p-3 scrollbar-thin">
          {!messages ? (
            <Skeleton className="h-24 w-full" />
          ) : messages.length === 0 ? (
            <p className="py-6 text-center text-sm text-ink-muted">No messages on this lead.</p>
          ) : (
            <ol className="space-y-2">
              {messages.slice(-12).map((m) => (
                <li key={m.id} className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-ink-faint">{m.role}</span>
                  <span className="ml-2 whitespace-pre-wrap text-ink">{m.body}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </section>

      <div className="mt-5 flex items-center justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        {handoff.status !== "resolved" && (
          <Button variant="primary" onClick={resolve} disabled={update.isPending}>
            Mark resolved
          </Button>
        )}
      </div>
    </div>
  );
}
